import ArticleNavigation from './ArticleNavigation';
import LeadForm from './LeadForm';
import PageMeta from './PageMeta';
import { RedesignFooter, RedesignHeader, RedesignPage } from './RedesignShell';

function cleanMarkdownLine(value) {
  return value.trim().replace(/^#{1,6}\s+/, '').trim();
}

function stripSourceMarker(value) {
  return value.replace(/^\[(?:ТЕКСТ|TEXT)\]\s*/i, '').trim();
}

function cleanTableCell(value) {
  return stripSourceMarker(value.trim().replace(/^#{1,6}\s+/, '').trim());
}

function parseTableRow(value) {
  return value.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map(cleanTableCell);
}

function parseArticleSource(source) {
  const data = {
    introBlocks: [],
    tocItems: [],
    sections: [],
    leadCta: '',
    hasLeadForm: false,
  };
  let bodyStarted = false;
  let inToc = false;
  let currentSection = null;
  let tableRows = [];

  const pushBlock = (block) => {
    if (currentSection) {
      currentSection.blocks.push(block);
      return;
    }
    data.introBlocks.push(block);
  };

  const flushTable = () => {
    if (!tableRows.length) {
      return;
    }
    pushBlock({ type: 'table', rows: tableRows });
    tableRows = [];
  };

  source.split(/\r?\n/).forEach((rawLine) => {
    const raw = rawLine.trim();
    if (!raw) {
      flushTable();
      return;
    }

    if (raw.startsWith('|')) {
      if (bodyStarted && !inToc) {
        tableRows.push(parseTableRow(raw));
      }
      return;
    }

    flushTable();
    const line = cleanMarkdownLine(raw);
    if (!line) {
      return;
    }

    const heading = line.match(/^\[?H([1-6])\]?\s*(?:[-:]\s*)?(.*)$/i);
    if (heading) {
      const level = Number(heading[1]);
      const text = heading[2].trim();
      if (level === 1) {
        bodyStarted = true;
        inToc = false;
        return;
      }
      if (!bodyStarted || !text) {
        return;
      }
      inToc = false;
      if (level === 2) {
        currentSection = { title: text, blocks: [] };
        data.sections.push(currentSection);
        return;
      }
      if (level === 3) {
        pushBlock({ type: 'h3', text });
        return;
      }
      pushBlock({ type: 'p', text });
      return;
    }

    if (!bodyStarted) {
      return;
    }

    if (/^\[?\s*СОДЕРЖАНИЕ\s*\]?$/i.test(line)) {
      inToc = true;
      return;
    }

    if (inToc) {
      const item = stripSourceMarker(line);
      if (item) {
        data.tocItems.push(item);
      }
      return;
    }

    const cta = line.match(/^\[CTA\]\s*(.+)$/i);
    if (cta) {
      data.leadCta = cta[1].trim();
      return;
    }

    if (/^Форма заявки$/i.test(line)) {
      data.hasLeadForm = true;
      return;
    }

    const text = stripSourceMarker(line);
    if (!text) {
      return;
    }

    if (/^ВАЖНО!/i.test(text)) {
      pushBlock({ type: 'important', text });
      return;
    }

    pushBlock({ type: 'p', text });
  });

  flushTable();
  return data;
}

function slugify(articleKey, index) {
  return `${articleKey}-section-${index + 1}`;
}

function ArticleTable({ rows }) {
  const [head, ...body] = rows;

  return (
    <div className="price-table-scroll article-table-scroll">
      <div className={`article-metric-table article-metric-table-${head.length}`}>
        <div className="article-metric-row article-metric-head">
          {head.map((cell) => (
            <span key={cell}>{cell}</span>
          ))}
        </div>
        {body.map((row) => (
          <div className="article-metric-row" key={row.join('|')}>
            {row.map((cell) => (
              <span key={cell}>{cell}</span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function ArticleBlock({ block }) {
  if (block.type === 'h3') {
    return <h3 className="article-section-h3">{block.text}</h3>;
  }

  if (block.type === 'important') {
    return (
      <aside className="article-important">
        <p>{block.text}</p>
      </aside>
    );
  }

  if (block.type === 'table') {
    return <ArticleTable rows={block.rows} />;
  }

  return <p>{block.text}</p>;
}

function TzArticlePage({ articleKey, metaTitle, metaDescription, articleTitle, source, taskPlaceholder, banner }) {
  const articleData = parseArticleSource(source);

  return (
    <RedesignPage>
      <PageMeta title={metaTitle} description={metaDescription} />
      <RedesignHeader />

      <section className="ink article-detail-hero" data-screen-label={`${articleKey} article hero`}>
        <div
          className="glow"
          style={{ right: '-340px', top: '-320px', width: '940px', height: '940px', opacity: 0.1 }}
        />
        <div className="container">
          <div className="eyebrow cyan">— статья</div>
          <h1>{articleTitle}</h1>
          {articleData.introBlocks.map((block) => (
            <p className="hero-sub" key={block.text}>
              {block.text}
            </p>
          ))}
        </div>
      </section>

      <section className="canvas sec-pad article-shell" data-screen-label={`${articleKey} article content`}>
        {banner && (
          <div className="container">
            <img className="case-cover-image article-cover-image" src={banner} alt="" width="1920" height="1080" decoding="async" />
          </div>
        )}
        <div className="container article-layout">
          <ArticleNavigation items={articleData.sections.map((section, index) => ({ id: slugify(articleKey, index), title: articleData.tocItems[index] || section.title }))} />

          <article className="article-content">
            {articleData.sections.map((section, index) => (
              <section className="article-section" tabIndex={-1} id={slugify(articleKey, index)} key={section.title}>
                <h2>{section.title}</h2>
                <div className="case-rich-text article-rich-text">
                  {section.blocks.map((block, blockIndex) => (
                    <ArticleBlock block={block} key={`${section.title}-${blockIndex}`} />
                  ))}
                </div>
              </section>
            ))}
          </article>
        </div>
      </section>

      <section className="ink lead article-lead" id={`${articleKey}-lead`} data-screen-label={`${articleKey} article lead`}>
        <div
          className="glow"
          style={{ left: '-300px', top: '-200px', width: '700px', height: '700px', opacity: 0.1 }}
        />
        <div className="container" style={{ position: 'relative' }}>
          <div className="lead-grid">
            <div className="lead-side">
              <div className="eyebrow cyan">— форма заявки</div>
              <div className="lead-heading">Форма заявки</div>
              {articleData.leadCta && <p className="lead-text">{articleData.leadCta}</p>}
            </div>
            <LeadForm
              formId={`${articleKey}-lead-form`}
              idPrefix={articleKey}
              submitLabel="Оставьте заявку"
              taskPlaceholder={taskPlaceholder}
            />
          </div>
        </div>
      </section>

      <RedesignFooter />
    </RedesignPage>
  );
}

export default TzArticlePage;
